import React, { useState } from "react";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/shared/ui/sheet";
import { NavLinks, NavUserMenu } from "./PortalNav";

/**
 * PortalMobileNav — top-of-page hamburger trigger that opens a
 * left-side Sheet drawer with the portal nav links and user menu.
 *
 * Mounted in PortalLayout at all viewports but visually hidden at
 * `lg+` via Tailwind classes — no JS branching, so SSR/initial paint
 * stays consistent. The Sheet itself uses Radix Dialog under the hood
 * (focus trap + Esc-to-close come for free).
 *
 * Devil's-advocate checks:
 *   - "What if a user resizes from mobile to desktop with the drawer
 *     open?" The Sheet stays open (stale state) but PortalLayout's
 *     desktop sidebar reappears alongside; no broken layout. We close
 *     on link tap regardless to avoid the user having to click twice.
 *   - "Is the trigger reachable on a phone with a notch?" The fixed
 *     top bar uses `pt-[env(safe-area-inset-top)]` so the hamburger
 *     button sits below the notch on iOS Safari.
 */
const PortalMobileNav: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="lg:hidden sticky top-0 z-30 flex items-center gap-3 bg-paper-panel border-b border-ink-line px-4 py-2"
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      data-testid="portal-mobile-nav"
    >
      <Sheet open={open} onOpenChange={setOpen}>
        <button
          type="button"
          aria-label="Open navigation"
          onClick={() => setOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-body hover:bg-ink-line motion-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong"
          data-testid="portal-mobile-nav-trigger"
        >
          <Menu className="h-5 w-5" />
        </button>
        <SheetContent
          side="left"
          className="motion-pop flex flex-col w-[260px] sm:max-w-[260px] p-0 bg-paper-panel"
          data-testid="portal-mobile-nav-sheet"
        >
          <SheetTitle className="sr-only">Portal navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Jump to a portal page.
          </SheetDescription>
          <div className="flex items-center justify-center py-6 px-4">
            {/* Invert the dark-inked logo to a white silhouette in dark. */}
            <img
              src="/codeClearLogo.svg"
              alt="Code Clear"
              className="h-8 w-auto dark:brightness-0 dark:invert"
            />
          </div>
          <NavLinks onNavigate={() => setOpen(false)} />
          <NavUserMenu onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <img
        src="/codeClearLogo.svg"
        alt="Code Clear"
        className="h-6 w-auto dark:brightness-0 dark:invert"
        aria-hidden="true"
      />
    </div>
  );
};

export default PortalMobileNav;
